import React, { useState, useEffect, useMemo } from "react";
import { Container, Row, Col, Button, Modal, Form, Alert, Badge, Table } from "react-bootstrap";
import { fetchReasons, createReason, updateReason } from "./DataService";

const TYPE_LABEL = { pickup: "Ramassage", delivery: "Livraison" };

function DeliveryIssueReasons() {
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [form, setForm] = useState({ type: "pickup", category: "", label: "" });

  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await fetchReasons();
      setReasons(data);
    } catch (e) {
      setError("Impossible de charger les motifs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const grouped = useMemo(() => {
    const byType = { pickup: {}, delivery: {} };
    reasons.forEach((r) => {
      if (!byType[r.type][r.category]) byType[r.type][r.category] = [];
      byType[r.type][r.category].push(r);
    });
    Object.values(byType).forEach((categories) => {
      Object.values(categories).forEach((list) => list.sort((a, b) => a.sort_order - b.sort_order));
    });
    return byType;
  }, [reasons]);

  const handleToggleActive = async (reason) => {
    try {
      const updated = await updateReason(reason.id, { active: !reason.active });
      setReasons((prev) => prev.map((r) => (r.id === reason.id ? updated : r)));
    } catch (e) {
      setError("Erreur lors de la mise à jour.");
    }
  };

  const handleMove = async (list, index, direction) => {
    const target = list[index + direction];
    if (!target) return;
    const current = list[index];

    try {
      const [updatedCurrent, updatedTarget] = await Promise.all([
        updateReason(current.id, { sort_order: target.sort_order }),
        updateReason(target.id, { sort_order: current.sort_order }),
      ]);
      setReasons((prev) =>
        prev.map((r) => {
          if (r.id === updatedCurrent.id) return updatedCurrent;
          if (r.id === updatedTarget.id) return updatedTarget;
          return r;
        })
      );
    } catch (e) {
      setError("Erreur lors du réordonnancement.");
    }
  };

  const handleSubmit = async () => {
    setFormError(null);
    if (!form.category || !form.label) {
      setFormError("La catégorie et le libellé sont obligatoires.");
      return;
    }
    setSaving(true);
    try {
      const created = await createReason(form);
      setReasons((prev) => [...prev, created]);
      setShowModal(false);
      setForm({ type: "pickup", category: "", label: "" });
    } catch (e) {
      const msg = e?.response?.data?.errors ? Object.values(e.response.data.errors)[0]?.[0] : "Erreur lors de l'ajout.";
      setFormError(msg);
    } finally {
      setSaving(false);
    }
  };

  const renderType = (type) => (
    <div className="mb-4" key={type}>
      <h5 className="mb-3">{TYPE_LABEL[type]}</h5>
      {Object.entries(grouped[type]).length === 0 ? (
        <div className="text-muted small">Aucun motif.</div>
      ) : (
        Object.entries(grouped[type]).map(([category, list]) => (
          <div key={category} className="mb-3">
            <div className="text-muted small fw-semibold mb-1">{category}</div>
            <Table size="sm" bordered className="mb-0">
              <tbody>
                {list.map((reason, index) => (
                  <tr key={reason.id}>
                    <td style={{ width: 90 }}>
                      <Button
                        variant="link"
                        size="sm"
                        disabled={index === 0}
                        onClick={() => handleMove(list, index, -1)}
                      >
                        ↑
                      </Button>
                      <Button
                        variant="link"
                        size="sm"
                        disabled={index === list.length - 1}
                        onClick={() => handleMove(list, index, 1)}
                      >
                        ↓
                      </Button>
                    </td>
                    <td className={!reason.active ? "text-muted" : ""}>{reason.label}</td>
                    <td style={{ width: 140 }}>
                      <Badge bg={reason.active ? "success" : "secondary"}>
                        {reason.active ? "Actif" : "Inactif"}
                      </Badge>
                    </td>
                    <td style={{ width: 120 }}>
                      <Button variant="outline-secondary" size="sm" onClick={() => handleToggleActive(reason)}>
                        {reason.active ? "Désactiver" : "Activer"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ))
      )}
    </div>
  );

  return (
    <Container fluid className="py-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-0">Motifs de problème</h4>
          <small className="text-muted">
            Raisons proposées au livreur quand un ramassage ou une livraison ne s'est pas bien passé.
            Une raison déjà utilisée n'est jamais supprimée — seulement désactivée.
          </small>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={() => setShowModal(true)}>
            + Ajouter
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}
      {loading && <p className="text-muted">Chargement…</p>}

      {!loading && (
        <>
          {renderType("pickup")}
          {renderType("delivery")}
        </>
      )}

      <Modal show={showModal} onHide={() => { setShowModal(false); setFormError(null); }}>
        <Modal.Header closeButton>
          <Modal.Title>Ajouter un motif</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError && <Alert variant="danger">{formError}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Type</Form.Label>
              <Form.Select
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
              >
                <option value="pickup">Ramassage</option>
                <option value="delivery">Livraison</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Catégorie</Form.Label>
              <Form.Control
                type="text"
                placeholder="ex: Accès"
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Libellé</Form.Label>
              <Form.Control
                type="text"
                placeholder="ex: Accès chantier impossible"
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowModal(false); setFormError(null); }}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saving}>
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default DeliveryIssueReasons;
